import { Box, Button, createStyles, Group, Text, TextInput, UnstyledButton } from "@mantine/core";
import { IconPlus, IconSearch } from "@tabler/icons-react";

import { useDisclosure } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import AddButton from "../../../components/AddButton";
import CustomPagination from "../../../components/Pagination/CustomPagination";
import PatientModal from "../../../components/PatientModal";
import PatientTable from "../../../components/PatientTable";
import Topbar from "../../../components/Topbar";
import { getAllFacilitiesModal, getAllFacilityTypes } from "../../../services";
import { getAllPatients } from "../../../services/patient";
import FacilityModal from "./FacilityModal";
import ClientForm from "./ClientForm";
import { useLocation, useNavigate } from "react-router-dom";
import { pageInfoAnalytics } from "../../../config/mixpanelConfig";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import swal from "sweetalert";
import {
  getSubscription,
  upgradePlanAddOn,
} from "../../../services/subscription";
import { isStaff } from "../../../http";
import { getAllPlans } from "../../../services/supportplan";

const useStyles = createStyles((theme) => ({
  content: {
    display: "flex",
    width: "100%",
    flexDirection: "column",
    overflowX: "auto",
  },
  formContent: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  pageBody: {
    padding: "22px 24px 40px",
    minWidth: 0,
    "@media (max-width: 760px)": {
      padding: "16px",
    },
  },
  controlsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 16,
    flexWrap: "wrap",
    "@media (max-width: 760px)": {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  controlsLeft: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
  },
  tabsPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: 4,
    gap: 4,
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 10,
    boxShadow: "0 1px 3px rgba(0,0,0,.06)",
  },
  tabPill: {
    padding: "8px 18px",
    background: "transparent",
    border: 0,
    borderRadius: 7,
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: "-0.005em",
    color: "var(--app-text-muted)",
    cursor: "pointer",
    transition: "background .15s ease, color .15s ease",
    "&:hover": { background: "var(--app-surface-2)", color: "var(--app-text)" },
  },
  tabPillActive: {
    background: "var(--app-text)",
    color: "var(--app-bg)",
    "&:hover": { background: "var(--app-text)", color: "var(--app-bg)" },
  },
  seatsLeft: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 11px 5px 8px",
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 999,
    fontSize: 12.5,
    color: "var(--app-text-muted)",
    fontWeight: 500,
    letterSpacing: "-0.003em",
    whiteSpace: "nowrap",
    boxShadow: "0 1px 3px rgba(0,0,0,.06)",
  },
  seatsCount: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 22,
    height: 22,
    padding: "0 7px",
    borderRadius: 999,
    background: "var(--app-text)",
    color: "var(--app-bg)",
    fontSize: 12,
    fontWeight: 800,
  },
  controlsRight: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    "@media (max-width: 760px)": {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  searchInput: {
    width: 280,
    "& input": {
      height: 40,
      borderRadius: 8,
      border: "1px solid var(--app-border)",
      background: "var(--app-surface)",
      fontFamily: "Inter",
      fontSize: 13.5,
      color: "var(--app-text)",
      "&::placeholder": { color: "var(--app-text-muted)" },
      "&:focus": { borderColor: "var(--app-primary)" },
    },
    "@media (max-width: 760px)": {
      width: "100%",
    },
  },
  addBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    height: 40,
    padding: "0 16px",
    background: "var(--app-primary)",
    border: 0,
    borderRadius: 8,
    color: "var(--app-on-primary)",
    fontFamily: "Inter",
    fontSize: 13.5,
    fontWeight: 700,
    cursor: "pointer",
    transition: "background .12s ease",
    "&:hover": { background: "color-mix(in srgb, var(--app-primary) 88%, #000)" },
  },
  table: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minHeight: 300,
  },
  paginationRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginTop: 16,
    padding: "4px",
    flexWrap: "wrap",
  },
  paginationInfo: {
    fontSize: 12.5,
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
    "& strong": { color: "var(--app-text)", fontWeight: 700 },
  },
}));
const Patient = (props) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [facility, setFacility] = useState("");
  const { patients, activePatientsCount } = useSelector(
    (state) => state.patient,
  );
  const { profile } = useSelector((state) => {
    return state.profile;
  });
  const [showClientForm, setShowClientForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [editAll, setEditAll] = useState(false);
  const [selectedTab, setSelectedTab] = useState(true);
  const [remainingClientCount, setRemainingClientCount] = useState(0);
  const [subscription, setSubscription] = useState(null);

  const location = useLocation();

  useEffect(() => {
    if (location?.state?.edit) {
      setShowClientForm(true);
      setEditingPatient(location?.state?.clientDetails);

      const ids = location?.state?.clientDetails?.facilityId?.map((fac) => {
        return fac?.id;
      });
      // setPopulateFacility(ids)
      setUpdateFacility(ids);
    } else {
      setShowClientForm(false);
    }
  }, [location?.state]);

  useEffect(() => {
    getAllFacilitiesModal("active");
    getAllFacilityTypes();
    pageInfoAnalytics("Client Page Viewed", "Client Page");
  }, []);

  useEffect(() => {
    getAllPatients(currentPage, true, selectedTab);
  }, [currentPage, selectedTab]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  const { classes } = useStyles();
  const [opened, { open, close }] = useDisclosure(false);
  const [options, setOptions] = useState([]);
  const { allFacilities } = useSelector((state) => state?.facility);
  const [
    facilityModalOpened,
    { open: openFacilityModal, close: closeFacilityModal },
  ] = useDisclosure(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [populateName, setPopulateName] = useState(null);
  const [populateFacility, setPopulateFacility] = useState(null);
  const [updateFacility, setUpdateFacility] = useState(null);

  useEffect(() => {
    const resp = [];
    allFacilities?.facilities?.map((val) => {
      if (val?.active) {
        resp?.push({
          name: val?.name,
          id: val?.id,
        });
      } else {
        resp?.push({
          name: val?.name,
          id: val?.id,
          disabled: true,
        });
      }
    });
    setOptions(resp);
  }, [allFacilities?.facilities]);

  useEffect(() => {
    if (editingPatient) {
      getAllPlans(editingPatient?.id);
    }
  }, [editingPatient]);

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const subs = await getSubscription();
        setSubscription(subs);
      } catch (error) {
        console.error("Error fetching subscription:", error);
      }
    };

    fetchSubscription();
  }, []);

  useEffect(() => {
    const allowedpatients =
      (profile?.subscription
        ? Number(profile?.subscription?.packageId?.patient)
        : Number(subscription?.packageId?.patient)) +
      (profile?.user?.type === "staff"
        ? Number(profile?.admin?.additionalClient || 0)
        : Number(profile?.user?.additionalClientCount || 0));
    setRemainingClientCount(allowedpatients - activePatientsCount);
  }, [activePatientsCount, profile, subscription]);

  const handleNextClick = (value) => {
    setFacility(value?.map((val) => val?.id));
    closeFacilityModal();
    setPopulateFacility(value);
    setShowClientForm(true);
  };

  const handleBackToClients = () => {
    setShowClientForm(false);
    setEditingPatient(null);
    setFacility("");
    setPopulateName(null);
    setCurrentPage(1);
    setEditAll(false);
    getAllPatients(currentPage, true, selectedTab);
    navigate("/home/patient", { state: { edit: false, disable: false } });
  };

  const handleEditPatient = (patient) => {
    setEditAll(true);
    setShowClientForm(true);
    setEditingPatient(patient);
    setPopulateFacility(patient?.facilityId);

    if (Array.isArray(patient?.facilityId)) {
      const ids = patient?.facilityId?.map((fac) => {
        return fac?.id;
      });

      // setPopulateFacility(ids)
      setUpdateFacility(ids);
    } else {
      // setPopulateFacility([patient?.facilityId?.id])
      setUpdateFacility([patient?.facilityId?.id]);
    }
  };

  const handleOpenModalClick = () => {
    openFacilityModal();
  };

  const handleTabChange = (tab) => {
    setCurrentPage(1);
    setSelectedTab(tab);
  };

  const handleAddClient = async () => {
    if (remainingClientCount <= 0) {
      if (profile?.subscription?.packageId?.name === "Starter") {
        await swal({
          text: `You have reached your quota upgrade subscription to increase limits`,
          icon: "warning",
          dangerMode: true,
        });
      } else {
        const willContinue = await swal({
          title: `Do you wish to continue?`,
          text: `You have exceeded your current plan limit. Additional client will cost $${
            profile?.user?.additionalClientCost ??
            profile?.subscription?.packageId?.additionalClient
          } per month. This charge will be added to your monthly subscription cost`,
          icon: "warning",
          dangerMode: true,
          buttons: {
            cancel: "Cancel",
            confirm: {
              text: "Confirm",
              value: true,
              closeModal: false,
            },
          },
        });

        if (willContinue) {
          await upgradePlanAddOn({ type: "client" });
          swal.close();
          openFacilityModal();
          setPopulateFacility(false);
          setEditingPatient(null);
          setUpdateFacility(null);
          setPopulateName(false);
          setEditAll(false);
        }
      }
    } else {
      openFacilityModal();
      setPopulateFacility(false);
      setEditingPatient(null);
      setUpdateFacility(null);
      setPopulateName(false);
      setEditAll(false);
    }
  };

  return (
    <Box className={classes.content}>
      {showClientForm ? (
        <Box className={classes.formContent}>
          <Topbar
            populateName={populateName}
            initialValues={editingPatient}
            populateFacility={populateFacility}
            backAllClients={handleBackToClients}
            onEditFacilityClick={handleOpenModalClick}
            title={editingPatient ? "Edit Client" : "Add Client"}
          />
          <ClientForm
            opened={opened}
            page={currentPage}
            facility={facility}
            currentPage={currentPage}
            close={handleBackToClients}
            initialValues={editingPatient}
            getAllPatients={getAllPatients}
            setPopulateName={setPopulateName}
            backAllClients={handleBackToClients}
            setShowClientForm={setShowClientForm}
            selectedTab={editAll ? false : location.state}
            buttonLabel={editingPatient ? "Update Client" : "Add Client"}
            tabName={selectedTab}
            populateFacility={populateFacility}
          />
        </Box>
      ) : (
        <>
          <PatientModal
            close={close}
            opened={opened}
            page={currentPage}
            title={"Add New Individual"}
          />
          <AppNavbar title="Client" profile={profile} />

          <Box className={classes.pageBody}>
            {/* Controls row: tabs + seats + search + add */}
            <Box className={classes.controlsRow}>
              {/* Left: tab pills + seats remaining */}
              <Box className={classes.controlsLeft}>
                <Box className={classes.tabsPill} role="tablist" aria-label="Client status">
                  <UnstyledButton
                    className={`${classes.tabPill} ${selectedTab ? classes.tabPillActive : ""}`}
                    onClick={() => handleTabChange(true)}
                    role="tab"
                    aria-selected={selectedTab}
                  >
                    Active
                  </UnstyledButton>
                  <UnstyledButton
                    className={`${classes.tabPill} ${!selectedTab ? classes.tabPillActive : ""}`}
                    onClick={() => handleTabChange(false)}
                    role="tab"
                    aria-selected={!selectedTab}
                  >
                    Inactive
                  </UnstyledButton>
                </Box>

                {!isStaff(profile?.user?.role) && (
                  <span className={classes.seatsLeft} aria-live="polite">
                    <span className={classes.seatsCount}>{remainingClientCount ?? 0}</span>
                    client seats left
                  </span>
                )}
              </Box>

              {/* Right side: search + add */}
              <Box className={classes.controlsRight}>
                <TextInput
                  className={classes.searchInput}
                  placeholder="Search clients…"
                  icon={<IconSearch size={16} color="#9CA3AF" />}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.currentTarget.value)}
                />
                {(profile?.user?.type !== "staff" ||
                  (profile?.user?.type === "staff" &&
                    profile?.user?.role === "admin")) && (
                  <UnstyledButton
                    className={classes.addBtn}
                    onClick={handleAddClient}
                  >
                    <IconPlus size={16} stroke={2.5} />
                    Add Client
                  </UnstyledButton>
                )}
              </Box>
            </Box>

            {/* Table + pagination */}
            <Box className={classes.table}>
              <PatientTable
                onEdit={handleEditPatient}
                selectedTab={selectedTab}
                searchQuery={searchQuery}
              />
              <Box className={classes.paginationRow}>
                <span className={classes.paginationInfo}>
                  {(() => {
                    const total = patients?.count || 0;
                    if (total === 0) return <>Showing <strong>0</strong> clients</>;
                    const start = (currentPage - 1) * 10 + 1;
                    const end = Math.min(currentPage * 10, total);
                    return <>Showing <strong>{start}–{end}</strong> of <strong>{total}</strong> clients</>;
                  })()}
                </span>
                <CustomPagination
                  itemsPerPage={10}
                  data={patients?.count}
                  currentPage={currentPage}
                  handlePageChange={handlePageChange}
                />
              </Box>
            </Box>
          </Box>
        </>
      )}
      <FacilityModal
        options={options}
        onNext={handleNextClick}
        close={closeFacilityModal}
        opened={facilityModalOpened}
        setUpdateFacility={setUpdateFacility}
        setEditingPatient={setEditingPatient}
        setPopulateFacility={setPopulateFacility}
        initialSelectedFacilities={populateFacility}
        editPatient={editingPatient ? editingPatient : false}
        editFacility={editingPatient ? updateFacility : false}
        title={editingPatient ? "Update Facility" : "Select a Facility"}
      />
    </Box>
  );
};

export default Patient;
